import { useEffect, useState } from "react";

import { Button, Popover, PopoverContent, PopoverTrigger } from "@upstageai/ui";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";

import { ConfidenceScoreSlider } from "./ConfidenceScoreSlider";
import { Icon, Icons } from "../../components/Icon";
import { BaseTooltip } from "../../components/Tooltip/BaseTooltip";
import { envVarStore } from "../../utils/mobx/EnvVarStore";
import { parsingTableStore } from "../../utils/mobx/ParsingTableStore";

const ConfidenceScoreInfoTooltip = () => {
  const { t } = useTranslation();
  return (
    <BaseTooltip
      tooltipContent={
        <div className="w-[400px]">
          {t("detail_header.confidence_description1")}
          &nbsp;
          {t("detail_header.confidence_description2")}
        </div>
      }
    >
      <Icon source={Icons.QuestionMarkCircle} />
    </BaseTooltip>
  );
};

/* ConfidenceScore의 기준을 바꿀수 있는 JSX를 반환하는 함수입니다.
 */
const ConfidenceScoreSetting = () => {
  const { t } = useTranslation();
  const { confidenceThreshold, setConfidenceThreshold } = parsingTableStore;
  const { initialConfidence } = envVarStore;

  useEffect(() => {
    setConfidenceThreshold(initialConfidence);
  }, []);

  /* confidenceThreshold 값을 내림해주는 함수.
   */
  useEffect(() => {
    confidenceTextSetter(Math.floor(confidenceThreshold * 100));
  }, [confidenceThreshold]);

  const [confidenceText, setConfidenceText] = useState<string>(
    t(`confidence.normal`),
  );

  const confidenceTextSetter = (confidence: number) => {
    switch (true) {
      case confidence >= 95:
        setConfidenceText(t(`confidence.very_strict`));
        break;
      case confidence >= 90:
        setConfidenceText(t(`confidence.strict`));
        break;
      case confidence >= 60:
        setConfidenceText(t(`confidence.normal`));
        break;
      case confidence >= 1:
        setConfidenceText(t(`confidence.lenient`));
        break;
      default:
        setConfidenceText(t(`confidence.n/a`));
        break;
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" className={"z-10 flex items-center gap-1.5"}>
          <span>{t(`common.confidence_score`)}</span>
          <span className="w-4 text-accent-red">
            {Math.floor(confidenceThreshold * 100)}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        onOpenAutoFocus={e => e.preventDefault()}
        className="rounded-xl"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-1">
            <span className="body-medium">{t(`common.confidence_score`)}</span>
            <ConfidenceScoreInfoTooltip />
          </div>

          <div className="w-full">
            <ConfidenceScoreSlider />
          </div>

          <div className="flex items-center justify-between gap-4">
            <span className="list text-accent-red">
              {Math.floor(confidenceThreshold * 100)}
            </span>
            <span className="p-ui-medium">{confidenceText}</span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default observer(ConfidenceScoreSetting);
